import * as React from 'react';
import { translate, InjectedTranslateProps } from 'react-i18next';
import { connect, ConnectedProps } from 'react-redux';
import * as css from './ImageViewer.scss';
import Delete from 'wix-ui-icons-common/Delete';
import Replace from 'wix-ui-icons-common/Replace';
import { MediaPlatform } from 'media-platform-js-sdk/browser';
import { getAccountId } from '../../redux/selectors/config';
import { ImageViewerLoader } from './ImageViewerLoader';
import { State } from '../../redux/configure-store';
import { createImageUrl, getImageFileName, getImagePath } from '../../services/image-helper';

import { IconButton, AddItem, Tooltip, Text } from 'wix-style-react';

interface ImageViewerProps extends InjectedTranslateProps, ConnectedProps<typeof connector> {
  imageUrl: string;
  onAddImage: Function;
  onUpdateUploadStatus: Function;
  onRemoveImage: Function;
}

interface ComponentState {
  loadingProgress: number;
  isLoading: boolean;
}

class ImageViewer extends React.Component<ImageViewerProps, ComponentState> {

  fileUploader;

  constructor(props: ImageViewerProps) {
    super(props);
    this.state = {
      loadingProgress: 0,
      isLoading: false,
    };
  }

  componentDidMount() {
    this.fileUploader = React.createRef();
  }

  updateLoadingStatus(isLoading: boolean) {
    this.setState({ isLoading });
    this.props.onUpdateUploadStatus(isLoading);
  }

  onFileChanged = (event: any) => {
    if (event.target.files.length < 1) {
      return;
    }
    this.updateLoadingStatus(true);
    this.setState({ loadingProgress: 0 });
    const imageFile = event.target.files[0];
    const path = getImagePath(this.props.accountId, encodeURIComponent(imageFile.name));
    const mediaPlatform = new MediaPlatform({
      domain: 'wixmp-a9a8500ac7c5cd8136e17898.appspot.com',
      authenticationUrl: `${window.location.origin}/_api/roles-management-web/team/media/auth-header`,
    });

    (mediaPlatform as any).fileManager.uploadFile(path, imageFile)
      .on('upload-progress', ({ loaded, total }) => {
        const loadingProgress = Math.round(90 * loaded / total);
        this.setState({ loadingProgress });
      })
      .on('upload-success', () => {
        this.setState({ loadingProgress: 100 });
        setTimeout(() => {
          let profileImage = createImageUrl(path);
          this.props.onAddImage(profileImage);
          this.updateLoadingStatus(false);
        }, 700);
      });

  }

  render() {
    let { t, imageUrl, onRemoveImage } = this.props;
    const { isLoading, loadingProgress } = this.state;
    const addScenario = !imageUrl;
    const editScenario = !!imageUrl;

    return (
      <div data-hook="team-image-input" className={`${css.wrapper} ${editScenario ? css.editScenarioWrapper : ''} ${addScenario ? css.addScenarioWrapper : ''}`}>
        <input
          ref={_fileUploader => this.fileUploader = _fileUploader}
          type="file"
          accept="image/*"
          onChange={this.onFileChanged}
          style={{ display: 'none' }}
        />
        {addScenario && <div className={css.add}>
          <AddItem dataHook="add-image"
                   removePadding
                   disabled={isLoading}
                   showIcon={false}
                   alignItems="left"
                   size="tiny"
                   theme="plain"
                   tooltipContent=" tooltip content" onClick={() => this.fileUploader.click()}>
            <div className={css.addItemContent}>
              {isLoading && <div className={css.addScenarioLoader}><ImageViewerLoader progress={loadingProgress}/></div>}
              {!isLoading && <div className={css.plusWrapper}>
                <svg width="48" height="48">
                  <path fill="#3899EC" d="M25 15v9h9v1h-9v9h-1v-9h-9v-1h9v-9z"/>
                </svg>
              </div>}
              {isLoading && <div><Text skin="disabled">{t('uploading')}</Text></div>}
              {!isLoading && <div><Text weight="normal"><a>{t('teamDetailsModal.upload')}</a></Text></div>}
            </div>
          </AddItem>
        </div>}
        {editScenario &&
        <div className={`${css.edit} ${isLoading ? css.editLoading : ''}`} data-hook="edit-image">
          <div className={css.imageWrapper}>
            {isLoading && <ImageViewerLoader scenario="update" progress={loadingProgress}/>}
            {!isLoading && <div className={css.img}>
              <img data-hook="team-image" src={imageUrl} width="48" height="48"/>
            </div>}
            {isLoading && <Text ellipsis light secondary>{t('uploading')}</Text>}
            {!isLoading && <Text ellipsis light secondary>{getImageFileName(this.props.accountId, imageUrl)}</Text>}
          </div>
          <div className={css.buttons}>
            <Tooltip content={t('remove')}>
              <IconButton dataHook="remove-image" priority="secondary" onClick={() => {
                this.fileUploader.value = '';
                return onRemoveImage();
              }}
                          disabled={isLoading}><Delete/></IconButton>
            </Tooltip>
            <Tooltip content={t('replace')}>
              <IconButton dataHook="update-image" priority="secondary" disabled={isLoading}
                          onClick={() => this.fileUploader.click()}>
                <Replace/>
              </IconButton>
            </Tooltip>
          </div>
        </div>}
      </div>
    );
  }
}

const mapStateToProps = (state: State) => ({
  accountId: getAccountId(state)
});

const connector = connect(mapStateToProps);
export default connector(translate()(ImageViewer));
